<template>
  <div class="border rounded shadow bg-white" id="lm-comments">
    <div class="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
      <i class="mdi" :class="icon" style="font-size: 1.5rem"></i>
      <h4 class="text-center m-0">{{ title }}</h4>
      <span class="clickable" @click="$emit('close')">
        <i class="mdi mdi-close" style="font-size: 1.5rem"></i>
      </span>
    </div>
    <LmComponentLoading v-if="isLoading" />
    <div class="scroll p-4" v-if="!!comments.length" v-chat-scroll="{ smooth: true }">
      <b-card v-for="(comment, index) in comments" :key="comment._id" no-body>
        <b-card-body class="p-3">
          <div class="d-flex mb-4 justify-content-between">
            <div class="d-flex">
              <b-avatar class="mr-3" :src="comment.createdBy.user.avatar" :text="avatarText(comment.createdBy.user)" variant="info"></b-avatar>
              <div>
                <h5 class="mt-0 mb-1">{{ fullName(comment.createdBy.user) }}</h5>
                <b-card-text class="text-muted datetime-text">{{ dateTimeFormat(comment.createdAt) }}</b-card-text>
              </div>
            </div>

            <b-dropdown
              v-if="!comment.deleted"
              right
              text="Right align"
              variant="link"
              toggle-class="text-decoration-none shadow-none text-body p-0"
              no-caret
              boundary="viewport"
            >
              <template #button-content>
              <i class="mdi mdi-dots-vertical clickable" style="font-size: 22px"></i><span class="sr-only">Opciones</span>
              </template>
              <b-dropdown-item @click="onReplyComment(comment)">Responder</b-dropdown-item>
              <b-dropdown-item
                v-if="userCanDelete(comment.createdBy.user)"
                variant="danger"
                @click="onDeleteComment(comment, index)"
              >
                Eliminar
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div v-html="comment.message" id="comments-message">
          </div>
        </b-card-body>
      </b-card>
    </div>
    <div v-else class="p-4 no-comments">
      <LmSvgIcon
        wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column"
        width="6em"
        icon="LmNoDataIcon"
        text="No hay comentarios, ¿Por qué no escribes uno?"
      />
    </div>
    <div class="px-4 py-2 m-0 d-flex flex-column border-top">
      <LmEditor
        hideMenus
        useMentions
        scrollable
        :editorHeight="100"
        v-model="message"
        placeholder="Escribe tu mensaje aquí..."
        ref="lmEditor"
        :disabled="disabled"
        @mentions-list="onUsersMentioned"
      />
      <LmButton
        buttonClass="btn-primary"
        class="align-self-end mt-2"
        :value="$t('comment')"
        :disabled="disabled || postingMessage"
        @click="onAddComment"
      />
    </div>
  </div>
</template>

<script>
// Components
import LmEditor from '@/components/form/elements/lmEditor'
import LmButton from '@/components/form/elements/LmButton'

// Vuex
import { mapGetters } from 'vuex'

// Utils
import commonsApi from '@/api/commons'
import moment from 'moment'
import 'moment/locale/es'
moment.locale('es')

export default {
  components: {
    LmEditor,
    LmButton
  },
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    linkType: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      postingMessage: false,
      comments: [],
      message: null,
      mentions: {
        click_action: this.$route.path,
        mentioned: []
      }
    }
  },
  computed: {
    ...mapGetters('session', ['userInfo'])
  },
  methods: {
    onUsersMentioned (event) {
      this.mentions.mentioned = event
    },
    async onAddComment () {
      try {
        this.postingMessage = true
        if (this.message) {
          const dataToSendToServer = {
            message: this.message,
            linkId: this.$route.params.id,
            linkType: this.linkType,
            lumbreMentions: this.mentions
          }
          const comment = await commonsApi.axiosPostWrapper('/comments', dataToSendToServer)
          this.comments.push(comment.data)
          this.message = null
          this.$emit('input', this.comments)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.postingMessage = false
      }
    },
    onReplyComment (comment) {
      const quoteReply = `<blockquote>${comment.message}</blockquote>`
      const userReply = `<span data-type="mention" class="mention" data-id="${comment.createdBy.user.id}" data-label="${this.fullName(comment.createdBy.user)}" contenteditable="false">${this.fullName(comment.createdBy.user)}</span>&ensp;`
      this.message = quoteReply + userReply

      this.$nextTick(() => {
        this.$refs.lmEditor.editor.commands.focus('end')
      })
    },
    async onDeleteComment (comment, index) {
      try {
        await commonsApi.axiosPutWrapper(`/comments/delete/${comment._id}`)
        this.comments.at(index).deleted = true
        this.comments.at(index).message = '<p class="text-muted font-italic">Comentario eliminado.</p>'
      } catch (error) {
        console.log(error)
      }
    },
    dateTimeFormat (datetime) {
      return moment(datetime).format('YYYY-MM-DD HH:mm')
    },
    avatarText (user) {
      return user.firstname.charAt(0) + user.lastname.charAt(0)
    },
    fullName (user) {
      return `${user.firstname} ${user.lastname}`
    },
    userCanDelete (user) {
      return user.id === this.userInfo.id
    },
    async getComments () {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosGetWrapper(`/comments/all/${this.linkType}/${this.$route.params.id}`)
        this.comments = res.data
      } catch (error) {
        throw error
      } finally {
        this.isLoading = false
      }
    }
  },
  async created () {
    try {
      await this.getComments()
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass">
  #lm-comments
    height: 100%
    .no-comments
      height: calc(100% - 200px - 67px)
    .scroll
      height: calc(100% - 200px - 67px)
      overflow-y: auto
      margin: 2px 0
      &:hover
        &::-webkit-scrollbar-thumb
          border-radius: 20px
          background: #888
      &::-webkit-scrollbar
        width: 8px
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: transparent
      &::-webkit-scrollbar-thumb:hover
        background: #555
    .datetime-text
      font-size: .7rem
    #comments-message
      blockquote
        padding-left: 1rem
        border-left: 2px solid rgba(13, 13, 13, 0.1)
</style>
